import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ArrowLeft, ArrowRight, Calendar, Clock, MessageSquare, Rss, Search, Sparkles, X } from 'lucide-react';
import { GET_CONTENTS } from '../services/queries';
import { useAuth } from '../contexts/AuthContext';
// import Breadcrumb from '../components/Breadcrumb'; // ✅ Nascosto insieme alla hero section
import LazyImage from '../components/LazyImage';
import SkeletonCard from '../components/SkeletonCard';
import { getAllStaticArticles, type StaticArticle } from '../data/staticArticles';

interface BlogArticle {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  tag: string;
  image: string;
  link: string;
}

const BlogPage: React.FC = () => {
  const { user } = useAuth();

  // 🆕 Stati per funzionalità avanzate - Infinite Scroll
  const [displayedArticles, setDisplayedArticles] = useState(9); // Inizia con 9 articoli
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const hasMoreRef = useRef(true); // ✅ Ref per tracciare se ci sono altri articoli

  // Determina i filtri in base al ruolo dell'utente
  const getQueryVariables = () => {
    // ✅ SEMPLIFICATO: Carichiamo TUTTI gli articoli dal DB in una volta sola
    // La paginazione è gestita solo lato frontend con displayedArticles
    const baseVars = {
      limit: 100, // Carichiamo tutti (massimo ragionevole)
      offset: 0,
      status: 'PUBLISHED' // Mostra solo articoli pubblicati nel blog pubblico
    };

    // Se l'utente non è loggato o è un ospite, mostra solo contenuti pubblici
    if (!user || user.roleId?.name === 'ospite' || user.roleId?.name === 'guest') {
      return {
        ...baseVars,
        visibility: 'PUBLIC'
      };
    }

    // Se è un membro, mostra contenuti pubblici e per membri
    if (user.roleId?.name === 'membro' || user.roleId?.name === 'member') {
      return baseVars; // Il backend gestirà automaticamente PUBLIC + MEMBERS
    }

    // Se è admin, mostra tutto (nessun filtro di visibilità)
    if (user.roleId?.name === 'amministratore' || user.roleId?.name === 'admin') {
      return baseVars; // Il backend gestirà automaticamente tutto
    }

    // Fallback: solo pubblici
    return {
      ...baseVars,
      visibility: 'PUBLIC'
    };
  };

  // Query GraphQL per ottenere gli articoli
  const { loading, error, data } = useQuery(GET_CONTENTS, {
    variables: getQueryVariables()
  });

  // DEBUG: Log per capire cosa sta succedendo
  console.log('🔍 BlogPage Debug:');
  console.log('Query variables:', getQueryVariables());
  console.log('GraphQL loading:', loading);
  console.log('Articles count:', data?.contents?.length || 0);

  // Carica articoli statici dalla nuova struttura modulare (useMemo per evitare ricreazione)
  const staticArticles: BlogArticle[] = useMemo(() => {
    const staticArticlesData = getAllStaticArticles();

    return staticArticlesData.map((article: StaticArticle) => ({
      id: article.id,
      title: article.title,
      excerpt: article.excerpt,
      body: article.body, // ✅ Aggiungi body per calcolo tempo di lettura
      date: new Date(article.publishedAt).toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      // ✅ Mantieni i campi originali per l'ordinamento
      publishedAt: article.publishedAt,
      createdAt: article.createdAt,
      tag: article.category.name,
      image: article.featuredImage,
      link: `/blog/${article.slug}`
    }));
  }, []); // Array vuoto perché gli articoli statici non cambiano

  // Combina articoli GraphQL e statici
  const allArticles = useMemo(() => {
    // Usa direttamente data.contents (ora carichiamo tutti con limit: 100)
    const dbArticles = data?.contents || [];

    // Combina tutti gli articoli (statici + database)
    const combined = [...staticArticles, ...dbArticles];

    // Ordina per data di pubblicazione (dal più recente al più vecchio)
    const sorted = combined.sort((a, b) => {
      // Determina la data per ogni articolo
      const dateA = a.publishedAt || a.createdAt;
      const dateB = b.publishedAt || b.createdAt;

      // Converti in timestamp per confronto
      const timeA = new Date(dateA).getTime();
      const timeB = new Date(dateB).getTime();

      // Ordine decrescente (più recente prima)
      return timeB - timeA;
    });

    return sorted;
  }, [data?.contents, staticArticles]);

  // 🔍 Filtra articoli per ricerca e categoria
  const filteredArticles = useMemo(() => {
    return allArticles.filter(article => {
      const matchesSearch = searchTerm === '' ||
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());

      const isGraphQLArticle = !!article.createdAt && !article.date;
      const articleCategory = isGraphQLArticle
        ? (article.category?.name || 'Generale')
        : article.tag;

      const matchesCategory = selectedCategory === '' ||
        articleCategory.toLowerCase() === selectedCategory.toLowerCase();

      return matchesSearch && matchesCategory;
    });
  }, [allArticles, searchTerm, selectedCategory]);

  // 📄 Articoli da visualizzare (con infinite scroll)
  const articles = useMemo(() => {
    return filteredArticles.slice(0, displayedArticles);
  }, [filteredArticles, displayedArticles]);

  // SEMPLIFICATO: hasMore controlla solo se ci sono altri articoli da visualizzare
  const hasMore = displayedArticles < filteredArticles.length;

  // ✅ Aggiorna il ref quando cambia hasMore
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // 🏷️ Estrae categorie uniche per il filtro
  const categories = useMemo(() => {
    const cats = allArticles.map(article => {
      const isGraphQLArticle = !!article.createdAt && !article.date;
      return isGraphQLArticle
        ? (article.category?.name || 'Generale')
        : article.tag;
    });
    return Array.from(new Set(cats)).sort();
  }, [allArticles]);

  // ⏱️ Calcola tempo di lettura stimato
  const calculateReadingTime = (text: string): number => {
    if (!text || text.trim().length === 0) return 1;

    const wordsPerMinute = 200; // Velocità media di lettura in italiano

    // Rimuovi tag HTML per contare solo il testo
    const cleanText = text.replace(/<[^>]*>/g, ' ');

    // Conta le parole (split su spazi e filtra stringhe vuote)
    const words = cleanText.split(/\s+/).filter(word => word.length > 0).length;

    // Calcola minuti (minimo 1 minuto)
    const minutes = Math.ceil(words / wordsPerMinute);
    return Math.max(1, minutes);
  };

  // 🔄 Infinite Scroll - Intersection Observer
  useEffect(() => {
    // ⚠️ NON creare l'observer finché i dati non sono caricati
    if (loading) return;

    const currentRef = loadMoreRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // ✅ Controlla solo isIntersecting, le altre condizioni sono gestite dallo stato
        if (entries[0].isIntersecting && hasMoreRef.current) {
          // Usa una callback per accedere sempre allo stato più recente
          setIsLoadingMore(prev => {
            // Se già in caricamento, non fare nulla
            if (prev) return prev;

            // Avvia il caricamento - SEMPLIFICATO: paginazione solo frontend
            setTimeout(() => {
              setDisplayedArticles(current => current + 9);
              setIsLoadingMore(false);
            }, 300);

            return true;
          });
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(currentRef);

    return () => {
      observer.unobserve(currentRef);
    };
  }, [loading]);

  // Reset quando cambiano filtri
  useEffect(() => {
    setDisplayedArticles(9);
  }, [searchTerm, selectedCategory]);

  // Dati di visualizzazione comuni ad articoli GraphQL e statici
  const datiArticolo = (article: any) => {
    // ✅ Migliore rilevamento: articoli GraphQL hanno createdAt, statici hanno date
    const isGraphQLArticle = !!article.createdAt && !article.date;
    const opzioniData: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    const displayDate = isGraphQLArticle
      ? new Date(article.publishedAt || article.createdAt).toLocaleDateString('it-IT', opzioniData)
      : article.date;
    const displayImage = isGraphQLArticle
      ? (article.featuredImage || '/images/logo-unasca.png')
      : article.image;
    const displayTag = isGraphQLArticle
      ? (article.category?.name || 'Generale')
      : article.tag;
    const displayLink = isGraphQLArticle
      ? `/blog/${article.slug || article.id}` // ✅ Fallback su ID se slug mancante
      : article.link;
    // ⏱️ Tempo di lettura: usa valore dal backend per articoli GraphQL, calcola per articoli statici
    const readingTime = isGraphQLArticle && article.readingTime
      ? article.readingTime
      : calculateReadingTime(article.body || article.content || '');
    return { isGraphQLArticle, displayDate, displayImage, displayTag, displayLink, readingTime };
  };

  const MetaArticolo: React.FC<{ data: string; minuti: number; chiaro?: boolean }> = ({ data, minuti, chiaro }) => (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${chiaro ? 'text-white/75' : 'text-gray-500 dark:text-gray-400'}`}>
      <span className="flex items-center gap-1.5">
        <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
        {data}
      </span>
      <span className="flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5" aria-hidden="true" />
        {minuti} min
      </span>
    </div>
  );

  const filtriAttivi = !!(searchTerm || selectedCategory);
  // In apertura, senza filtri, l'articolo più recente diventa la copertina
  const copertina = !filtriAttivi && articles.length > 0 ? articles[0] : null;
  const elenco = copertina ? articles.slice(1) : articles;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="bg-primary-950 py-12">
          <div className="container-custom">
            <div className="h-10 w-64 animate-pulse rounded-lg bg-white/10" />
          </div>
        </div>
        <div className="container-custom py-10" aria-busy="true">
          <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">Caricamento articoli...</p>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state (usa articoli statici come fallback)
  if (error) {
    console.warn('GraphQL Error, using static articles:', error);
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Testata compatta: gli articoli restano in primo piano */}
      <section className="relative overflow-hidden bg-primary-950">
        <div
          className="absolute inset-0 opacity-10 text-primary-300"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }}
          aria-hidden="true"
        />
        <div className={`relative container-custom pt-10 md:pt-12 ${copertina ? 'pb-28 md:pb-32' : 'pb-10 md:pb-12'}`}>
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-white">
                Blog
              </h1>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-white/75">
                Articoli informativi, eventi dei Consigli Nazionali, comunicazioni ufficiali e aggiornamenti per gli associati
              </p>
            </div>
            <a
              href="https://studi.unasca.it/rss.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 self-start rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white ring-1 ring-white/20 transition-colors hover:bg-white/20 md:self-auto"
              title="Iscriviti al feed RSS"
            >
              <Rss className="h-4 w-4 text-orange-400" aria-hidden="true" />
              RSS
            </a>
          </div>
        </div>
      </section>

      <div className="container-custom pb-20">
        {/* Articolo di copertina */}
        {copertina && (() => {
          const d = datiArticolo(copertina);
          return (
            <Link
              to={d.displayLink}
              className="group relative -mt-20 mb-12 grid overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-gray-900 dark:ring-white/10 lg:grid-cols-12"
            >
              <div className="relative aspect-video overflow-hidden lg:col-span-7 lg:aspect-auto lg:min-h-[22rem]">
                <LazyImage
                  src={d.displayImage}
                  alt={copertina.title}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 motion-safe:group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold text-white shadow">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                  Ultimo articolo
                </span>
              </div>
              <div className="flex flex-col justify-center p-7 md:p-10 lg:col-span-5">
                <p className="mb-3 text-xs font-semibold text-primary-700 dark:text-primary-400">{d.displayTag}</p>
                <h2 className="mb-4 text-2xl md:text-3xl font-bold leading-tight tracking-tight text-gray-900 transition-colors group-hover:text-primary-700 dark:text-white dark:group-hover:text-primary-400">
                  {copertina.title}
                </h2>
                <p className="mb-6 line-clamp-4 text-base leading-relaxed text-gray-600 dark:text-gray-400">
                  {copertina.excerpt}
                </p>
                <MetaArticolo data={d.displayDate} minuti={d.readingTime} />
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 dark:text-primary-400">
                  Leggi tutto
                  <ArrowRight className="h-4 w-4 transition-transform motion-safe:group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </div>
            </Link>
          );
        })()}

        {/* Ricerca e categorie */}
        <div className={`mb-10 ${copertina ? '' : 'pt-10'}`}>
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative flex-1">
              <label htmlFor="cerca-articoli" className="sr-only">Cerca articoli</label>
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                id="cerca-articoli"
                type="text"
                placeholder="Cerca articoli..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input w-full rounded-full pl-12"
              />
            </div>
            {filtriAttivi && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                  setDisplayedArticles(9);
                }}
                className="btn btn-ghost whitespace-nowrap"
              >
                <X className="mr-2 h-5 w-5" aria-hidden="true" />
                Cancella
              </button>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Filtra per categoria">
            {['', ...categories].map((category) => {
              const attiva = selectedCategory === category;
              return (
                <button
                  key={category || 'tutte'}
                  type="button"
                  aria-pressed={attiva}
                  onClick={() => setSelectedCategory(category)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-950 ${
                    attiva
                      ? 'bg-primary-700 text-white shadow'
                      : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-primary-50 hover:text-primary-800 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-800'
                  }`}
                >
                  {category || 'Tutte le categorie'}
                </button>
              );
            })}
          </div>

          {/* Results Count */}
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" aria-live="polite">
            {filtriAttivi ? (
              <>
                <strong className="text-gray-900 dark:text-white">{filteredArticles.length}</strong> articoli trovati
                {searchTerm && <span className="ml-1">per "{searchTerm}"</span>}
                {selectedCategory && <span className="ml-1">in "{selectedCategory}"</span>}
              </>
            ) : (
              <>
                {/* Mostra il totale reale degli articoli caricati */}
                <strong className="text-gray-900 dark:text-white">{allArticles.length}</strong> articoli disponibili
              </>
            )}
          </p>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {elenco.map((article: any) => {
            const d = datiArticolo(article);
            return (
              <Link
                to={d.displayLink}
                key={article.id}
                className="group rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-950"
              >
                <article className="flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5 transition-shadow duration-300 hover:shadow-xl dark:bg-gray-900 dark:ring-white/10">
                  <div className="relative aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800">
                    <LazyImage
                      src={d.displayImage}
                      alt={article.title}
                      className="h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-105"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-primary-800 shadow-sm backdrop-blur-sm dark:bg-gray-900/90 dark:text-primary-300">
                      {d.displayTag}
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col p-6">
                    <MetaArticolo data={d.displayDate} minuti={d.readingTime} />
                    {/* Title - Massimo 80 caratteri */}
                    <h3 className="mb-3 mt-3 line-clamp-2 text-lg font-semibold leading-snug text-gray-900 transition-colors group-hover:text-primary-700 dark:text-white dark:group-hover:text-primary-400">
                      {article.title.length > 80 ? article.title.substring(0, 80) + '...' : article.title}
                    </h3>
                    {/* Excerpt - Massimo 250 caratteri */}
                    <p className="mb-5 line-clamp-3 flex-1 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                      {article.excerpt.length > 250 ? article.excerpt.substring(0, 120) + '...' : article.excerpt}
                    </p>
                    <div className="flex items-center justify-between border-t border-gray-100 pt-4 dark:border-gray-800">
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 dark:text-primary-400">
                        Leggi tutto
                        <ArrowRight className="h-4 w-4 transition-transform motion-safe:group-hover:translate-x-1" aria-hidden="true" />
                      </span>
                      {d.isGraphQLArticle && article.metadata?.views && (
                        <span className="text-xs text-gray-500 dark:text-gray-500">
                          {article.metadata.views} views
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              </Link>
            );
          })}
        </div>

        {/* 🔄 Infinite Scroll - Loading Skeletons */}
        {isLoadingMore && (
          <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* 📍 Intersection Observer Target - SEMPRE visibile quando hasMore */}
        {hasMore && (
          <div ref={loadMoreRef} className="mt-12 flex h-20 items-center justify-center">
            {!isLoadingMore && (
              <div className="text-sm text-gray-400 dark:text-gray-600">
                Scorri per caricare altri articoli...
              </div>
            )}
          </div>
        )}

        {/* ✅ Fine articoli */}
        {!hasMore && articles.length > 0 && (
          <div className="mt-12 border-t border-gray-200 py-8 text-center dark:border-gray-800">
            <p className="text-gray-500 dark:text-gray-400">
              Hai visualizzato tutti i {filteredArticles.length} articoli disponibili
            </p>
          </div>
        )}

        {/* Messaggio se nessun articolo */}
        {articles.length === 0 && (
          <div className="rounded-2xl bg-white py-12 text-center shadow-sm ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
            <p className="text-gray-500 dark:text-gray-400">
              Nessun articolo disponibile al momento.
            </p>
          </div>
        )}

        {/* Messaggio di stato GraphQL */}
        {error && (
          <div className="mt-8 rounded-xl border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              ⚠️ Connessione al server in corso... Mostrando contenuti di esempio.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="relative mt-12 overflow-hidden rounded-3xl bg-gradient-to-r from-primary-950 via-primary-900 to-primary-800 p-8 md:p-10">
          <div className="relative flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="hidden h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20 sm:flex">
                <MessageSquare className="h-6 w-6 text-primary-300" aria-hidden="true" />
              </div>
              <p className="text-base md:text-lg text-white">
                Hai suggerimenti per nuovi articoli o vuoi segnalare un problema?
              </p>
            </div>
            <Link
              to="/"
              className="inline-flex flex-shrink-0 items-center rounded-full bg-white px-6 py-3 font-semibold text-primary-800 transition-colors hover:bg-primary-50"
            >
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Torna alla Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
